import { Layout } from "react-grid-layout";

import {
  WIDGET_LAYOUT_MIN_H,
  WIDGET_LAYOUT_MIN_W,
} from "@/features/admin/features/pages-management/utils";

export type GutterOrientation = "vertical" | "horizontal";

export type Gutter = {
  id: string;
  orientation: GutterOrientation;
  /** Boundary in grid units (column index for vertical, row index for horizontal). */
  boundary: number;
  /** Inclusive start along the other axis. */
  start: number;
  /** Exclusive end along the other axis. */
  end: number;
};

type BoundaryUnit = {
  blocked: boolean;
  primaryIds: Set<string>;
  secondaryIds: Set<string>;
};

type EdgeInterval = {
  widgetId: string;
  start: number;
  end: number;
};

type BoundaryClassification = {
  units: BoundaryUnit[];
  edgeIntervals: EdgeInterval[];
};

function emptyBoundaryUnit(): BoundaryUnit {
  return { blocked: false, primaryIds: new Set(), secondaryIds: new Set() };
}

function gutterRangesOverlap(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): boolean {
  return aStart < bEnd && bStart < aEnd;
}

function hasEdgeContact(unit: BoundaryUnit): boolean {
  return !unit.blocked && (unit.primaryIds.size > 0 || unit.secondaryIds.size > 0);
}

function hasBothSides(units: BoundaryUnit[], start: number, end: number): boolean {
  let hasPrimary = false;
  let hasSecondary = false;
  for (let index = start; index < end; index++) {
    if (units[index].primaryIds.size > 0) hasPrimary = true;
    if (units[index].secondaryIds.size > 0) hasSecondary = true;
  }
  return hasPrimary && hasSecondary;
}

function isOneSidedUnit(unit: BoundaryUnit): boolean {
  if (unit.blocked) return false;
  const hasPrimary = unit.primaryIds.size > 0;
  const hasSecondary = unit.secondaryIds.size > 0;
  return hasPrimary !== hasSecondary;
}

function gutterRangesTouch(
  aStart: number,
  aEnd: number,
  bStart: number,
  bEnd: number,
): boolean {
  return aEnd === bStart || bEnd === aStart;
}

function clipRangeToNonBlocked(
  units: BoundaryUnit[],
  rangeStart: number,
  rangeEnd: number,
): Array<{ start: number; end: number }> {
  const runs: Array<{ start: number; end: number }> = [];
  let runStart = -1;

  for (let index = rangeStart; index < rangeEnd; index++) {
    if (units[index].blocked) {
      if (runStart >= 0) {
        runs.push({ start: runStart, end: index });
        runStart = -1;
      }
      continue;
    }
    if (runStart < 0) runStart = index;
  }

  if (runStart >= 0) {
    runs.push({ start: runStart, end: rangeEnd });
  }

  return runs;
}

function splitAtBlocked(
  units: BoundaryUnit[],
  start: number,
  end: number,
): Array<{ start: number; end: number }> {
  const segments: Array<{ start: number; end: number }> = [];
  let runStart = -1;

  for (let index = start; index < end; index++) {
    if (units[index].blocked) {
      if (runStart >= 0) {
        segments.push({ start: runStart, end: index });
        runStart = -1;
      }
      continue;
    }
    if (runStart < 0) runStart = index;
  }

  if (runStart >= 0) {
    segments.push({ start: runStart, end: end });
  }

  return segments;
}

function mergeGutterSegments(
  segments: Array<{ start: number; end: number }>,
): Array<{ start: number; end: number }> {
  if (segments.length === 0) return [];

  const sorted = [...segments].sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: Array<{ start: number; end: number }> = [];

  for (const segment of sorted) {
    const last = merged[merged.length - 1];
    if (last && segment.start <= last.end) {
      last.end = Math.max(last.end, segment.end);
    } else {
      merged.push({ ...segment });
    }
  }

  return merged;
}

function classifyBoundary(
  layout: Layout[],
  boundary: number,
  orientation: GutterOrientation,
  spanLength: number,
): BoundaryClassification {
  const units: BoundaryUnit[] = Array.from({ length: spanLength }, () =>
    emptyBoundaryUnit(),
  );
  const edgeIntervals: EdgeInterval[] = [];

  for (const item of layout) {
    if (orientation === "vertical") {
      const straddles = item.x < boundary && item.x + item.w > boundary;
      const primary = item.x + item.w === boundary;
      const secondary = item.x === boundary;
      const perpStart = item.y;
      const perpEnd = item.y + item.h;

      if (primary || secondary) {
        edgeIntervals.push({ widgetId: item.i, start: perpStart, end: perpEnd });
      }

      for (let perp = perpStart; perp < perpEnd; perp++) {
        if (perp < 0 || perp >= spanLength) continue;
        const unit = units[perp];
        if (straddles) unit.blocked = true;
        if (primary) unit.primaryIds.add(item.i);
        if (secondary) unit.secondaryIds.add(item.i);
      }
    } else {
      const straddles = item.y < boundary && item.y + item.h > boundary;
      const primary = item.y + item.h === boundary;
      const secondary = item.y === boundary;
      const perpStart = item.x;
      const perpEnd = item.x + item.w;

      if (primary || secondary) {
        edgeIntervals.push({ widgetId: item.i, start: perpStart, end: perpEnd });
      }

      for (let perp = perpStart; perp < perpEnd; perp++) {
        if (perp < 0 || perp >= spanLength) continue;
        const unit = units[perp];
        if (straddles) unit.blocked = true;
        if (primary) unit.primaryIds.add(item.i);
        if (secondary) unit.secondaryIds.add(item.i);
      }
    }
  }

  return { units, edgeIntervals };
}

function isSeedUnit(unit: BoundaryUnit): boolean {
  return !unit.blocked && unit.primaryIds.size > 0 && unit.secondaryIds.size > 0;
}

function findSeedClusters(units: BoundaryUnit[]): Array<{ start: number; end: number }> {
  const clusters: Array<{ start: number; end: number }> = [];
  let index = 0;

  while (index < units.length) {
    if (!isSeedUnit(units[index])) {
      index += 1;
      continue;
    }
    const start = index;
    index += 1;
    while (index < units.length && isSeedUnit(units[index])) {
      index += 1;
    }
    clusters.push({ start, end: index });
  }

  return clusters;
}

function adjacentLipTouchMerge(
  units: BoundaryUnit[],
  segmentStart: number,
  segmentEnd: number,
  clippedStart: number,
  clippedEnd: number,
): boolean {
  if (!gutterRangesTouch(segmentStart, segmentEnd, clippedStart, clippedEnd)) {
    return false;
  }

  if (clippedStart === segmentEnd) {
    return (
      segmentEnd < units.length &&
      isOneSidedUnit(units[segmentEnd]) &&
      isSeedUnit(units[segmentEnd - 1])
    );
  }

  if (clippedEnd === segmentStart) {
    return (
      segmentStart > 0 &&
      isOneSidedUnit(units[segmentStart - 1]) &&
      isSeedUnit(units[segmentStart])
    );
  }

  return false;
}

function expandGutterSegment(
  layout: Layout[],
  boundary: number,
  orientation: GutterOrientation,
  spanLength: number,
  units: BoundaryUnit[],
  edgeIntervals: EdgeInterval[],
  seedStart: number,
  seedEnd: number,
): Array<{ start: number; end: number }> {
  let start = seedStart;
  let end = seedEnd;
  let changed = true;

  while (changed) {
    changed = false;

    for (const interval of edgeIntervals) {
      const overlaps = gutterRangesOverlap(start, end, interval.start, interval.end);
      const touchesEndpoint = adjacentLipTouchMerge(
        units,
        start,
        end,
        interval.start,
        interval.end,
      );

      if (!overlaps && !touchesEndpoint) continue;

      for (const clipped of clipRangeToNonBlocked(units, interval.start, interval.end)) {
        const clippedOverlaps = gutterRangesOverlap(start, end, clipped.start, clipped.end);
        const clippedTouches = adjacentLipTouchMerge(
          units,
          start,
          end,
          clipped.start,
          clipped.end,
        );

        if (!clippedOverlaps && !clippedTouches) continue;

        const nextStart = Math.min(start, clipped.start);
        const nextEnd = Math.max(end, clipped.end);
        if (nextStart < start || nextEnd > end) {
          start = nextStart;
          end = nextEnd;
          changed = true;
        }
      }
    }

    while (start > 0 && !units[start - 1].blocked) {
      const unit = units[start - 1];
      if (!hasEdgeContact(unit)) break;
      if (!hasBothSides(units, start - 1, end)) break;
      start -= 1;
      changed = true;
    }

    while (end < units.length && !units[end].blocked) {
      const unit = units[end];
      if (!hasEdgeContact(unit)) break;
      if (!hasBothSides(units, start, end + 1)) break;
      end += 1;
      changed = true;
    }
  }

  return splitAtBlocked(units, start, end);
}

function discoverSegmentsOnBoundary(
  layout: Layout[],
  boundary: number,
  orientation: GutterOrientation,
  spanLength: number,
): Array<{ start: number; end: number }> {
  const { units, edgeIntervals } = classifyBoundary(
    layout,
    boundary,
    orientation,
    spanLength,
  );

  const expanded: Array<{ start: number; end: number }> = [];
  for (const cluster of findSeedClusters(units)) {
    expanded.push(
      ...expandGutterSegment(
        layout,
        boundary,
        orientation,
        spanLength,
        units,
        edgeIntervals,
        cluster.start,
        cluster.end,
      ),
    );
  }

  return mergeGutterSegments(expanded);
}

function maxLayoutY(layout: Layout[]): number {
  return layout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
}

// ---------------------------------------------------------------------------
// Discovery
// ---------------------------------------------------------------------------

/**
 * Derive vertical and horizontal gutter handles from the current layout.
 *
 * Spawn rules (symmetric for both orientations):
 * - **Spawn** where two box edges meet on the boundary, even partially.
 * - **No through**: straddling boxes block the handle at that unit.
 * - **Edge completion**: when an edge interval overlaps the segment, merge its full span (split at blockers).
 *   When an interval merely touches a segment end, merge only for a one-sided **endpoint lip**
 *   (outside unit one-sided, inside unit dual at that end). Same rule at both segment ends.
 * - **Side-gap continuation**: extend through one-sided edge contact while the segment hugs both sides globally.
 * - **Stop** at blockers, grid edge, or hollow void (no both-sides hug).
 * - **No bends**: each handle is one contiguous straight segment; disjoint runs stay separate.
 * - **Merge** overlapping/adjacent segments on the same boundary into one handle.
 */
export function discoverGutters(layout: Layout[], cols: number): Gutter[] {
  if (layout.length === 0 || cols < 2) return [];

  const maxY = maxLayoutY(layout);
  const gutters: Gutter[] = [];

  for (let boundary = 1; boundary < cols; boundary++) {
    for (const { start, end } of discoverSegmentsOnBoundary(
      layout,
      boundary,
      "vertical",
      maxY,
    )) {
      gutters.push({
        id: `v:${boundary}:${start}:${end}`,
        orientation: "vertical",
        boundary,
        start,
        end,
      });
    }
  }

  for (let boundary = 1; boundary < maxY; boundary++) {
    for (const { start, end } of discoverSegmentsOnBoundary(
      layout,
      boundary,
      "horizontal",
      cols,
    )) {
      gutters.push({
        id: `h:${boundary}:${start}:${end}`,
        orientation: "horizontal",
        boundary,
        start,
        end,
      });
    }
  }

  return gutters;
}

type AttachBaseline = {
  item: Layout;
  deltaAtAttach: number;
};

export type GutterResizeSession = {
  gutter: Gutter;
  /** Layout at pointer-down. */
  snapshot: Layout[];
  /** Ids that abutted the gutter when the drag started. */
  initialPrimaryIds: ReadonlySet<string>;
  initialSecondaryIds: ReadonlySet<string>;
  /** Current participants (initial ∪ sticky attached − detached). */
  primaryIds: Set<string>;
  secondaryIds: Set<string>;
  /** Sticky attachments: geometry and delta when they joined. */
  attachBaselines: Map<string, AttachBaseline>;
  /**
   * Sticky ids that have left their attach geometry at least once this gesture.
   * Needed so we do not detach on the attach frame (geometry still equals snapshot).
   */
  movedSinceAttach: Set<string>;
  /** Detached after returning to snapshot size; may re-attach when boundary re-contacts. */
  detachedIds: Set<string>;
};

// ---------------------------------------------------------------------------
// Layout span helpers (used by apply / sticky membership)
// ---------------------------------------------------------------------------

function overlapsRange(start: number, size: number, unit: number): boolean {
  return start <= unit && unit < start + size;
}

/** Whether a layout item's projection overlaps [start, end) on one axis. */
export function itemOverlapsSpan(
  itemStart: number,
  itemSize: number,
  start: number,
  end: number,
): boolean {
  return itemStart < end && itemStart + itemSize > start;
}

export function overlapsUnit(itemStart: number, itemSize: number, unit: number): boolean {
  return overlapsRange(itemStart, itemSize, unit);
}

function resolveMinW(item: Layout): number {
  return item.minW ?? WIDGET_LAYOUT_MIN_W;
}

function resolveMinH(item: Layout): number {
  return item.minH ?? WIDGET_LAYOUT_MIN_H;
}

function withLayoutMins(item: Layout, snapshot: Layout): Layout {
  return {
    ...item,
    minW: snapshot.minW ?? WIDGET_LAYOUT_MIN_W,
    minH: snapshot.minH ?? WIDGET_LAYOUT_MIN_H,
  };
}

// ---------------------------------------------------------------------------
// Apply / sticky membership
// ---------------------------------------------------------------------------

export function initialPrimaryIds(layout: Layout[], gutter: Gutter): Set<string> {
  const ids = new Set<string>();
  for (const item of layout) {
    if (gutter.orientation === "vertical") {
      if (
        item.x + item.w === gutter.boundary &&
        itemOverlapsSpan(item.y, item.h, gutter.start, gutter.end)
      ) {
        ids.add(item.i);
      }
    } else if (
      item.y + item.h === gutter.boundary &&
      itemOverlapsSpan(item.x, item.w, gutter.start, gutter.end)
    ) {
      ids.add(item.i);
    }
  }
  return ids;
}

export function initialSecondaryIds(layout: Layout[], gutter: Gutter): Set<string> {
  const ids = new Set<string>();
  for (const item of layout) {
    if (gutter.orientation === "vertical") {
      if (
        item.x === gutter.boundary &&
        itemOverlapsSpan(item.y, item.h, gutter.start, gutter.end)
      ) {
        ids.add(item.i);
      }
    } else if (
      item.y === gutter.boundary &&
      itemOverlapsSpan(item.x, item.w, gutter.start, gutter.end)
    ) {
      ids.add(item.i);
    }
  }
  return ids;
}

export function createGutterResizeSession(
  layout: Layout[],
  gutter: Gutter,
): GutterResizeSession {
  const primaryIds = initialPrimaryIds(layout, gutter);
  const secondaryIds = initialSecondaryIds(layout, gutter);
  return {
    gutter,
    snapshot: layout.map((item) => ({
      ...item,
      minW: resolveMinW(item),
      minH: resolveMinH(item),
    })),
    initialPrimaryIds: new Set(primaryIds),
    initialSecondaryIds: new Set(secondaryIds),
    primaryIds,
    secondaryIds,
    attachBaselines: new Map(),
    movedSinceAttach: new Set(),
    detachedIds: new Set(),
  };
}

function snapshotMap(snapshot: Layout[]): Map<string, Layout> {
  return new Map(snapshot.map((item) => [item.i, item]));
}

function relativeDelta(session: GutterResizeSession, id: string, delta: number): number {
  const attached = session.attachBaselines.get(id);
  return attached ? delta - attached.deltaAtAttach : delta;
}

function baseItem(session: GutterResizeSession, id: string): Layout | undefined {
  const attached = session.attachBaselines.get(id);
  if (attached) return attached.item;
  return session.snapshot.find((item) => item.i === id);
}

function minSizeForItem(item: Layout, gutter: Gutter): number {
  return gutter.orientation === "vertical" ? resolveMinW(item) : resolveMinH(item);
}

/**
 * Clamp Δ so shrinking-side members stay ≥ min size.
 * Positive Δ grows primary (left/above) and shrinks secondary.
 */
export function clampGutterDelta(session: GutterResizeSession, delta: number): number {
  if (delta === 0) return 0;

  const { gutter, primaryIds, secondaryIds, snapshot } = session;
  const shrinkSize = gutter.orientation === "vertical" ? "w" : "h";
  const snap = snapshotMap(snapshot);

  if (delta > 0) {
    let max = delta;
    for (const id of secondaryIds) {
      const item = baseItem(session, id);
      if (!item) continue;
      const attach = session.attachBaselines.get(id);
      const size = item[shrinkSize];
      const min = minSizeForItem(snap.get(id) ?? item, gutter);
      const absMax = (attach?.deltaAtAttach ?? 0) + (size - min);
      max = Math.min(max, absMax);
    }
    return Math.max(0, max);
  }

  let min = delta;
  for (const id of primaryIds) {
    const item = baseItem(session, id);
    if (!item) continue;
    const attach = session.attachBaselines.get(id);
    const size = item[shrinkSize];
    const minSize = minSizeForItem(snap.get(id) ?? item, gutter);
    const absMin = (attach?.deltaAtAttach ?? 0) + (minSize - size);
    min = Math.max(min, absMin);
  }
  return Math.min(0, min);
}

function applyDeltaToLayout(session: GutterResizeSession, delta: number): Layout[] {
  const { gutter, snapshot, primaryIds, secondaryIds, detachedIds } = session;
  const snap = snapshotMap(snapshot);

  return snapshot.map((item) => {
    const snapshotItem = snap.get(item.i) ?? item;

    if (detachedIds.has(item.i)) {
      return withLayoutMins(snapshotItem, snapshotItem);
    }

    const base = baseItem(session, item.i) ?? item;
    const rel = relativeDelta(session, item.i, delta);

    if (gutter.orientation === "vertical") {
      if (primaryIds.has(item.i)) {
        return withLayoutMins({ ...base, w: base.w + rel }, snapshotItem);
      }
      if (secondaryIds.has(item.i)) {
        return withLayoutMins({ ...base, x: base.x + rel, w: base.w - rel }, snapshotItem);
      }
      return withLayoutMins(item, snapshotItem);
    }

    if (primaryIds.has(item.i)) {
      return withLayoutMins({ ...base, h: base.h + rel }, snapshotItem);
    }
    if (secondaryIds.has(item.i)) {
      return withLayoutMins({ ...base, y: base.y + rel, h: base.h - rel }, snapshotItem);
    }
    return withLayoutMins(item, snapshotItem);
  });
}

function sameGeometry(a: Layout, b: Layout, orientation: GutterOrientation): boolean {
  if (orientation === "vertical") {
    return a.x === b.x && a.w === b.w;
  }
  return a.y === b.y && a.h === b.h;
}

function itemWouldAttachAtBoundary(
  item: Layout,
  gutter: Gutter,
  movedBoundary: number,
): boolean {
  if (gutter.orientation === "vertical") {
    if (!itemOverlapsSpan(item.y, item.h, gutter.start, gutter.end)) {
      return false;
    }
    return item.x + item.w === movedBoundary || item.x === movedBoundary;
  }

  if (!itemOverlapsSpan(item.x, item.w, gutter.start, gutter.end)) {
    return false;
  }
  return item.y + item.h === movedBoundary || item.y === movedBoundary;
}

/**
 * Attach newly abutting widgets; detach sticky ones that returned to snapshot size.
 */
export function updateStickyMembership(
  session: GutterResizeSession,
  layout: Layout[],
  delta: number,
): GutterResizeSession {
  const { gutter, snapshot } = session;
  const snap = snapshotMap(snapshot);
  const primaryIds = new Set(session.primaryIds);
  const secondaryIds = new Set(session.secondaryIds);
  const detachedIds = new Set(session.detachedIds);
  const attachBaselines = new Map(session.attachBaselines);
  const movedSinceAttach = new Set(session.movedSinceAttach);

  const movedBoundary = gutter.boundary + delta;

  for (const id of [...detachedIds]) {
    const item = layout.find((entry) => entry.i === id);
    if (item && itemWouldAttachAtBoundary(item, gutter, movedBoundary)) {
      detachedIds.delete(id);
    }
  }

  for (const item of layout) {
    if (detachedIds.has(item.i)) continue;
    if (primaryIds.has(item.i) || secondaryIds.has(item.i)) continue;

    if (gutter.orientation === "vertical") {
      if (!itemOverlapsSpan(item.y, item.h, gutter.start, gutter.end)) continue;
      if (item.x + item.w === movedBoundary) {
        primaryIds.add(item.i);
        const snapshotItem = snap.get(item.i) ?? item;
        attachBaselines.set(item.i, {
          item: withLayoutMins({ ...item }, snapshotItem),
          deltaAtAttach: delta,
        });
      } else if (item.x === movedBoundary) {
        secondaryIds.add(item.i);
        const snapshotItem = snap.get(item.i) ?? item;
        attachBaselines.set(item.i, {
          item: withLayoutMins({ ...item }, snapshotItem),
          deltaAtAttach: delta,
        });
      }
    } else {
      if (!itemOverlapsSpan(item.x, item.w, gutter.start, gutter.end)) continue;
      if (item.y + item.h === movedBoundary) {
        primaryIds.add(item.i);
        const snapshotItem = snap.get(item.i) ?? item;
        attachBaselines.set(item.i, {
          item: withLayoutMins({ ...item }, snapshotItem),
          deltaAtAttach: delta,
        });
      } else if (item.y === movedBoundary) {
        secondaryIds.add(item.i);
        const snapshotItem = snap.get(item.i) ?? item;
        attachBaselines.set(item.i, {
          item: withLayoutMins({ ...item }, snapshotItem),
          deltaAtAttach: delta,
        });
      }
    }
  }

  for (const [id, attach] of attachBaselines) {
    if (delta !== attach.deltaAtAttach) {
      movedSinceAttach.add(id);
    }
  }

  const considerDetach = (id: string, side: "primary" | "secondary") => {
    if (side === "primary" && session.initialPrimaryIds.has(id)) return;
    if (side === "secondary" && session.initialSecondaryIds.has(id)) return;
    if (!attachBaselines.has(id)) return;
    // Still sitting at the attach delta with snapshot geometry — keep attached.
    if (!movedSinceAttach.has(id)) return;

    const original = snap.get(id);
    const current = layout.find((entry) => entry.i === id);
    if (!original || !current) return;

    if (sameGeometry(current, original, gutter.orientation)) {
      primaryIds.delete(id);
      secondaryIds.delete(id);
      attachBaselines.delete(id);
      movedSinceAttach.delete(id);
      detachedIds.add(id);
    }
  };

  for (const id of [...primaryIds]) considerDetach(id, "primary");
  for (const id of [...secondaryIds]) considerDetach(id, "secondary");

  return {
    ...session,
    primaryIds,
    secondaryIds,
    attachBaselines,
    movedSinceAttach,
    detachedIds,
  };
}

/**
 * Widget ids on the shrinking side that are at min size during a gutter drag.
 * Shown when the user moves the gutter in a direction that would shrink them further.
 */
export function getGutterSquishIds(
  session: GutterResizeSession,
  layout: Layout[],
  rawDelta: number,
): Set<string> {
  if (rawDelta === 0) return new Set();

  const { gutter, primaryIds, secondaryIds, snapshot } = session;
  const shrinkingIds = rawDelta > 0 ? secondaryIds : primaryIds;
  const sizeKey = gutter.orientation === "vertical" ? "w" : "h";
  const layoutById = new Map(layout.map((item) => [item.i, item]));
  const snap = snapshotMap(snapshot);
  const squish = new Set<string>();

  for (const id of shrinkingIds) {
    const current = layoutById.get(id);
    if (!current) continue;
    const mins = snap.get(id) ?? current;
    const min = minSizeForItem(mins, gutter);
    if (current[sizeKey] <= min) {
      squish.add(id);
    }
  }

  return squish;
}

/**
 * Apply a gutter move from the drag-start snapshot (plus sticky baselines).
 * Positive Δ grows the primary side and shrinks the secondary (classic splitter).
 */
export function applyGutterResize(
  session: GutterResizeSession,
  rawDelta: number,
): {
  layout: Layout[];
  session: GutterResizeSession;
  delta: number;
  squishIds: Set<string>;
} {
  const delta = clampGutterDelta(session, rawDelta);
  const layout = applyDeltaToLayout(session, delta);
  const nextSession = updateStickyMembership(session, layout, delta);
  const squishIds = getGutterSquishIds(nextSession, layout, rawDelta);
  return { layout, session: nextSession, delta, squishIds };
}

// ---------------------------------------------------------------------------
// Layout compaction (replaces RGL vertical compact on the edit page)
// ---------------------------------------------------------------------------

/** Mark grid row indices that contain at least one widget (any column). */
function occupiedRowIndices(layout: Layout[]): Set<number> {
  const occupied = new Set<number>();
  for (const item of layout) {
    for (let y = item.y; y < item.y + item.h; y++) {
      occupied.add(y);
    }
  }
  return occupied;
}

function occupiesCell(item: Layout, x: number, y: number): boolean {
  return x >= item.x && x < item.x + item.w && y >= item.y && y < item.y + item.h;
}

function neighbourPairKey(a: string, b: string): string {
  return a < b ? `${a}|${b}` : `${b}|${a}`;
}

/**
 * Widget pairs that share a vertical edge with overlapping row span
 * (horizontal neighbours on at least one row).
 */
export function horizontalNeighbourPairs(layout: Layout[]): Set<string> {
  const pairs = new Set<string>();

  for (let i = 0; i < layout.length; i++) {
    for (let j = i + 1; j < layout.length; j++) {
      const a = layout[i];
      const b = layout[j];
      const yOverlap = a.y < b.y + b.h && b.y < a.y + a.h;
      if (!yOverlap) continue;

      const aRight = a.x + a.w;
      const bRight = b.x + b.w;
      if (aRight === b.x || bRight === a.x) {
        pairs.add(neighbourPairKey(a.i, b.i));
      }
    }
  }

  return pairs;
}

/** Whether `itemId` can move up one row without collision or new horizontal neighbours. */
export function canMoveUp(layout: Layout[], itemId: string): boolean {
  const widget = layout.find((entry) => entry.i === itemId);
  if (!widget || widget.y === 0) {
    return false;
  }

  const newY = widget.y - 1;

  for (let x = widget.x; x < widget.x + widget.w; x++) {
    for (const other of layout) {
      if (other.i === itemId) continue;
      if (occupiesCell(other, x, newY)) {
        return false;
      }
    }
  }

  const beforePairs = horizontalNeighbourPairs(layout);
  const moved = layout.map((entry) =>
    entry.i === itemId ? { ...entry, y: newY } : { ...entry },
  );
  const afterPairs = horizontalNeighbourPairs(moved);

  for (const pair of afterPairs) {
    if (!beforePairs.has(pair)) {
      const [left, right] = pair.split("|");
      if (left === itemId || right === itemId) {
        return false;
      }
    }
  }

  return true;
}

function layoutGeometryEqual(a: Layout[], b: Layout[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  const byId = new Map(b.map((entry) => [entry.i, entry]));
  for (const item of a) {
    const other = byId.get(item.i);
    if (
      !other ||
      item.x !== other.x ||
      item.y !== other.y ||
      item.w !== other.w ||
      item.h !== other.h
    ) {
      return false;
    }
  }

  return true;
}

function compactCooperativeSlideOnce(layout: Layout[]): Layout[] {
  // Bottom-first: lower widgets move up before upper ones so existing neighbour
  // pairs (e.g. C|D on a shared row) are still present when each move is checked.
  const order = [...layout].sort((a, b) => b.y - a.y || a.x - b.x);
  let next = layout.map((entry) => ({ ...entry }));

  for (const item of order) {
    const current = next.find((entry) => entry.i === item.i);
    if (!current || !canMoveUp(next, current.i)) {
      continue;
    }

    next = next.map((entry) =>
      entry.i === current.i ? { ...entry, y: entry.y - 1 } : entry,
    );
  }

  return next;
}

/** Slide widgets up into column-aligned voids without creating new horizontal neighbours. */
export function compactCooperativeSlide(layout: Layout[]): Layout[] {
  let current = layout.map((entry) => ({ ...entry }));

  for (;;) {
    const next = compactCooperativeSlideOnce(current);
    if (layoutGeometryEqual(current, next)) {
      return current;
    }
    current = next;
  }
}

/**
 * Collapse fully empty horizontal grid rows by shifting widgets below them upward.
 *
 * Unlike react-grid-layout's vertical compactor, widgets are never pulled into
 * partial gaps in a row above (e.g. space beside a wider neighbour on the
 * previous row).
 */
export function compactClearEmptyRows(layout: Layout[], cols: number): Layout[] {
  if (layout.length === 0 || cols < 1) {
    return layout.map((item) => ({ ...item }));
  }

  const occupied = occupiedRowIndices(layout);
  if (occupied.size === 0) {
    return layout.map((item) => ({ ...item }));
  }

  const maxRow = Math.max(...occupied) + 1;
  const emptyRows: number[] = [];
  for (let y = 0; y < maxRow; y++) {
    if (!occupied.has(y)) {
      emptyRows.push(y);
    }
  }

  if (emptyRows.length === 0) {
    return layout.map((item) => ({ ...item }));
  }

  return layout.map((item) => {
    const shift = emptyRows.filter((row) => row < item.y).length;
    return shift === 0 ? { ...item } : { ...item, y: item.y - shift };
  });
}

/**
 * Edit-page compactor: cooperative upward slide (Phase 2) then empty-row collapse
 * (Phase 1), repeated until stable. Preserves existing horizontal neighbour pairs.
 */
export function compactLayout(layout: Layout[], cols: number): Layout[] {
  if (layout.length === 0 || cols < 1) {
    return layout.map((item) => ({ ...item }));
  }

  let current = layout.map((item) => ({ ...item }));

  for (;;) {
    const afterSlide = compactCooperativeSlide(current);
    const afterClear = compactClearEmptyRows(afterSlide, cols);
    if (layoutGeometryEqual(afterClear, current)) {
      return afterClear;
    }
    current = afterClear;
  }
}
